/**
 * Assign Lead Modal Component
 * Modal for assigning a lead to staff member(s)
 */

import React, { useState } from 'react';
import { X, UserCheck, User, Search, CheckCircle2 } from 'lucide-react';

const AssignLeadModal = ({ isOpen, onClose, onSubmit, isLoading, lead, staff = [] }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStaff, setSelectedStaff] = useState(lead?.assignedTo || []);
  const [notes, setNotes] = useState('');

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const data = {
      assigneeId: selectedStaff.length === 1 ? selectedStaff[0] : selectedStaff,
      notes: notes,
    };

    onSubmit(data);
  };

  const handleClose = () => {
    setSearchTerm('');
    setSelectedStaff(lead?.assignedTo || []);
    setNotes('');
    onClose();
  };

  const handleStaffToggle = (staffId) => {
    setSelectedStaff(prev => 
      prev.includes(staffId)
        ? prev.filter(id => id !== staffId)
        : [...prev, staffId]
    );
  };

  const handleSelectAll = () => {
    if (selectedStaff.length === filteredStaff.length) {
      setSelectedStaff([]);
    } else {
      setSelectedStaff(filteredStaff.map(s => s.id));
    }
  };

  // Filter staff by search term
  const filteredStaff = staff.filter(member => {
    const fullName = `${member.firstName} ${member.lastName}`.toLowerCase();
    const email = (member.email || '').toLowerCase();
    const term = searchTerm.toLowerCase();
    return fullName.includes(term) || email.includes(term);
  });

  // Get selected staff names for display
  const getSelectedNames = () => {
    return selectedStaff
      .map(id => {
        const member = staff.find(s => s.id === id);
        return member ? `${member.firstName} ${member.lastName}` : '';
      })
      .filter(Boolean)
      .join(', ');
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center mr-3">
              <UserCheck size={20} className="text-indigo-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Assign Lead</h2>
              <p className="text-sm text-gray-500">
                {lead ? `Assign ${lead.parentFirstName} ${lead.parentLastName}` : 'Assign this lead'}
              </p>
            </div>
          </div>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600">
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <form id="assign-form" onSubmit={handleSubmit} className="space-y-6">
            {/* Search */}
            <div>
              <label className="form-label">Search Staff</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="text"
                  className="form-input pl-10"
                  placeholder="Search by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            {/* Selected count */}
            {selectedStaff.length > 0 && (
              <div className="bg-indigo-50 rounded-lg p-3">
                <p className="text-sm text-indigo-900">
                  <span className="font-medium">{selectedStaff.length}</span> staff member{selectedStaff.length !== 1 ? 's' : ''} selected
                </p>
                <p className="text-xs text-indigo-600 mt-1 truncate">
                  {getSelectedNames()}
                </p>
              </div>
            )}

            {/* Staff List */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="form-label mb-0">Staff Members</label>
                {filteredStaff.length > 0 && (
                  <button
                    type="button"
                    onClick={handleSelectAll}
                    className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                  >
                    {selectedStaff.length === filteredStaff.length ? 'Deselect All' : 'Select All'}
                  </button>
                )}
              </div>
              
              <div className="border rounded-lg overflow-hidden">
                {filteredStaff.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    <User className="mx-auto h-12 w-12 text-gray-300 mb-3" />
                    <p>No staff members found</p>
                    {searchTerm && (
                      <button
                        type="button"
                        onClick={() => setSearchTerm('')}
                        className="text-indigo-600 hover:text-indigo-700 text-sm mt-2"
                      >
                        Clear search
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="max-h-64 overflow-y-auto">
                    {filteredStaff.map((member, index) => {
                      const isSelected = selectedStaff.includes(member.id);
                      return (
                        <div
                          key={member.id}
                          onClick={() => handleStaffToggle(member.id)}
                          className={`flex items-center p-3 cursor-pointer transition-colors ${
                            isSelected ? 'bg-indigo-50' : 'hover:bg-gray-50'
                          } ${index !== filteredStaff.length - 1 ? 'border-b' : ''}`}
                        >
                          <div className="flex-shrink-0">
                            {isSelected ? (
                              <CheckCircle2 size={20} className="text-indigo-600" />
                            ) : (
                              <div className="w-5 h-5 rounded-full border-2 border-gray-300" />
                            )}
                          </div>
                          
                          <div className="ml-3 flex-1">
                            <div className="flex items-center">
                              <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center mr-3">
                                <span className="text-sm font-medium text-gray-600">
                                  {member.firstName[0]}{member.lastName[0]}
                                </span>
                              </div>
                              <div>
                                <p className="font-medium text-gray-900">
                                  {member.firstName} {member.lastName}
                                </p>
                                <p className="text-sm text-gray-500">{member.email}</p>
                              </div>
                            </div>
                          </div>
                          
                          {member.role && (
                            <span className={`ml-2 px-2 py-0.5 rounded text-xs font-medium ${
                              member.role === 'admin' 
                                ? 'bg-purple-100 text-purple-700' 
                                : 'bg-gray-100 text-gray-700'
                            }`}>
                              {member.role}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Assignment Notes */}
            <div>
              <label className="form-label">
                Assignment Notes <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <textarea
                className="form-input"
                rows="3"
                placeholder="Add any notes about this assignment..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>

            {/* Unassign option */}
            {selectedStaff.length > 0 && (
              <button
                type="button"
                onClick={() => setSelectedStaff([])}
                className="text-sm text-red-600 hover:text-red-700 font-medium"
              >
                Unassign all staff
              </button>
            )}
          </form>
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 p-6 border-t border-gray-100">
          <button
            type="button"
            onClick={handleClose}
            className="btn-secondary"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="assign-form"
            disabled={isLoading}
            className="btn-primary disabled:opacity-50 flex items-center space-x-2"
          >
            <UserCheck size={18} />
            <span>{isLoading ? 'Assigning...' : 'Assign Lead'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssignLeadModal;
